import Modal from "@/components/Modal";
import PdfViewer from "@/components/PdfViewer";
import SkeletonRow from "@/components/SkeletonRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatRupiah } from "@/lib/utils";
import { useDataValidasi } from "@/store/dataValidasiStore";
import { DataKeluarga as DataKeluargaType } from "@/types";
import {
  CheckCircle2,
  Eye,
  FileText,
  ReceiptText,
  Users,
  Wallet,
} from "lucide-react";
import { useState } from "react";

export default function DataKeluarga({
  isPending,
  isPendingDataKeluargaTambahan,
  isValidPermanen,
}: {
  isPending: boolean;
  isPendingDataKeluargaTambahan: boolean;
  isValidPermanen: boolean;
}) {
  const {
    dataListKeluarga,
    setDataListKeluarga,
    dataKeluargaTambahan,
    setDataKeluargaTambahan,
  } = useDataValidasi();
  const [showPdf, setShowPdf] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataKeluargaType | null>(null);

  const handleValidasi = (id: string) => {
    const updatedData = dataListKeluarga.map((item) =>
      item.id_data_keluarga === id
        ? { ...item, isValidated: !item.isValidated }
        : item,
    );

    setDataListKeluarga(updatedData);

    const dataValidasiString = localStorage.getItem("dataValidasi");
    const previousData = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : {};
    const previousBiodata = previousData.biodata || [];
    const previousKeluargaTambahan = previousData.keluarga_tambahan || null;
    const previousAset = previousData.aset || [];
    const previousBerkas = previousData.berkas || [];

    const updatedDataValidasi = {
      biodata: previousBiodata,
      keluarga: updatedData,
      keluarga_tambahan: previousKeluargaTambahan,
      aset: previousAset,
      berkas: previousBerkas,
    };

    localStorage.setItem("dataValidasi", JSON.stringify(updatedDataValidasi));
  };

  const handleValidasiDataKeluargaTambahan = () => {
    if (!dataKeluargaTambahan) return;

    const updatedDataKeluargaTambahan = {
      ...dataKeluargaTambahan,
      isValidated: !dataKeluargaTambahan?.isValidated,
    };
    setDataKeluargaTambahan(updatedDataKeluargaTambahan);

    const dataValidasiString = localStorage.getItem("dataValidasi");
    const previousData = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : {};
    const previousBiodata = previousData.biodata || [];
    const previousKeluarga = previousData.keluarga || [];
    const previousAset = previousData.aset || [];
    const previousBerkas = previousData.berkas || [];

    const updatedDataValidasi = {
      biodata: previousBiodata,
      keluarga: previousKeluarga,
      keluarga_tambahan: updatedDataKeluargaTambahan,
      aset: previousAset,
      berkas: previousBerkas,
    };

    localStorage.setItem("dataValidasi", JSON.stringify(updatedDataValidasi));
  };

  if (isPending || isPendingDataKeluargaTambahan)
    return (
      <div className="flex flex-col gap-y-3">
        {Array(5)
          .fill(null)
          .map((_, index) => (
            <SkeletonRow key={index} />
          ))}
      </div>
    );

  return (
    <section className="p-2 sm:p-5 flex flex-col gap-8 w-full">
      {/* ==========================================
          SECTION 1: DAFTAR ANGGOTA KELUARGA
      ========================================== */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 px-1">
          <Users className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
            Daftar Anggota Keluarga
          </h2>
        </div>

        {/* --- TAMPILAN MOBILE (CARD) --- */}
        <div className="grid grid-cols-1 gap-4 md:hidden">
          {dataListKeluarga?.map((item, index) => (
            <div
              key={index}
              className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-4 flex flex-col gap-4"
            >
              {/* Header Card: Hubungan & Checkbox Validasi */}
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                <Badge
                  variant="secondary"
                  className="font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  {item.hubungan_keluarga?.hubungan_keluarga}
                </Badge>

                {/* Checkbox Validasi diletakkan di atas agar langsung terlihat */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                    Validasi:
                  </span>
                  <Checkbox
                    className={`h-5 w-5 rounded-md transition-colors ${
                      isValidPermanen || item.isValidated
                        ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                        : ""
                    }`}
                    checked={isValidPermanen || item.isValidated}
                    onCheckedChange={() =>
                      !isValidPermanen && handleValidasi(item.id_data_keluarga)
                    }
                  />
                </label>
              </div>

              {/* Body Card: Data Diri */}
              <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-sm">
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-xs text-slate-500 mb-0.5">Nama Lengkap</p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {item.nama}
                  </p>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-xs text-slate-500 mb-0.5">
                    Penghasilan / Bulan
                  </p>
                  <p className="font-mono font-medium text-slate-800 dark:text-slate-200">
                    {formatRupiah(Number(item.penghasilan_old))}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 mb-0.5">Pekerjaan</p>
                  <p className="text-slate-600 dark:text-slate-400">
                    {item.pekerjaan?.pekerjaan}
                  </p>
                </div>
              </div>

              {/* Footer Card: Tombol Dokumen */}
              <div className="pt-2">
                <Button
                  variant={item.path_slip_gaji ? "outline" : "ghost"}
                  size="sm"
                  className={`w-full h-9 ${!item.path_slip_gaji && "opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800/50"}`}
                  disabled={!item.path_slip_gaji}
                  onClick={() => {
                    if (item.path_slip_gaji) {
                      setShowPdf(true);
                      setSelectedRow(item);
                    }
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Lihat Berkas Slip Gaji
                </Button>
              </div>
            </div>
          ))}

          {(!dataListKeluarga || dataListKeluarga.length === 0) && (
            <div className="text-center p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500 text-sm">
              Data keluarga tidak ditemukan.
            </div>
          )}
        </div>

        {/* --- TAMPILAN DESKTOP (TABEL) --- */}
        <div className="hidden md:block bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">
                    Hubungan
                  </th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">
                    Nama Lengkap
                  </th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">
                    Pekerjaan
                  </th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">
                    Penghasilan / Bulan
                  </th>
                  <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                    Dokumen
                  </th>
                  <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                    Validasi
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {dataListKeluarga?.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Badge
                        variant="secondary"
                        className="font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                      >
                        {item.hubungan_keluarga?.hubungan_keluarga}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                      {item.nama}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {item.pekerjaan?.pekerjaan}
                    </td>
                    <td className="px-4 py-3 font-mono font-medium text-slate-800 dark:text-slate-200">
                      {formatRupiah(Number(item.penghasilan_old))}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant={item.path_slip_gaji ? "outline" : "ghost"}
                        size="sm"
                        className={`h-8 ${!item.path_slip_gaji && "opacity-50 cursor-not-allowed"}`}
                        disabled={!item.path_slip_gaji}
                        onClick={() => {
                          if (item.path_slip_gaji) {
                            setShowPdf(true);
                            setSelectedRow(item);
                          }
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" /> Lihat
                      </Button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center items-center h-full">
                        <Checkbox
                          className={`h-6 w-6 rounded-md transition-colors ${
                            isValidPermanen || item.isValidated
                              ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                              : ""
                          }`}
                          checked={isValidPermanen || item.isValidated}
                          onCheckedChange={() =>
                            !isValidPermanen &&
                            handleValidasi(item.id_data_keluarga)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {(!dataListKeluarga || dataListKeluarga.length === 0) && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      Data keluarga tidak ditemukan.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ==========================================
          SECTION 2: SUMMARY TANGGUNGAN & BIAYA
      ========================================== */}
      <div className="flex flex-col gap-3 mt-4">
        <div className="flex items-center gap-2 px-1">
          <FileText className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
            Informasi Tambahan
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card Biaya Hidup */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm flex items-start gap-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg shrink-0">
              <Wallet className="w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Biaya Hidup Perbulan
              </span>
              <span className="text-lg font-bold font-mono text-slate-900 dark:text-slate-100">
                {dataKeluargaTambahan?.biaya_hidup_keluarga_perbulan
                  ? formatRupiah(
                      Number(
                        dataKeluargaTambahan.biaya_hidup_keluarga_perbulan,
                      ),
                    )
                  : "Rp 0"}
              </span>
            </div>
          </div>

          {/* Card Tanggungan */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm flex items-start gap-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-lg shrink-0">
              <Users className="w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Jumlah Tanggungan
              </span>
              <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {dataKeluargaTambahan?.jumlah_tanggungan_orangtua_wali_text ||
                  "-"}
              </span>
            </div>
          </div>

          {/* Card Validasi Interaktif */}
          <label
            className={`rounded-xl p-4 border shadow-sm flex items-center justify-between cursor-pointer transition-all duration-200 group ${
              isValidPermanen || dataKeluargaTambahan?.isValidated
                ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900/50"
                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-primary/50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-full transition-colors ${
                  isValidPermanen || dataKeluargaTambahan?.isValidated
                    ? "bg-emerald-200/50 text-emerald-600 dark:text-emerald-400"
                    : "bg-slate-100 text-slate-400 dark:bg-slate-800"
                }`}
              >
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-sm font-bold ${
                    isValidPermanen || dataKeluargaTambahan?.isValidated
                      ? "text-emerald-800 dark:text-emerald-400"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                >
                  Validasi Data Tambahan
                </span>
                <span className="text-xs text-slate-500">
                  Tandai jika data sesuai
                </span>
              </div>
            </div>

            <Checkbox
              className={`h-6 w-6 rounded-md shadow-none border-slate-300 ${
                isValidPermanen || dataKeluargaTambahan?.isValidated
                  ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                  : "group-hover:border-primary"
              }`}
              checked={isValidPermanen || dataKeluargaTambahan?.isValidated}
              onCheckedChange={() =>
                !isValidPermanen && handleValidasiDataKeluargaTambahan()
              }
            />
          </label>
        </div>
      </div>

      {/* ==========================================
          MODAL PDF VIEWER
      ========================================== */}
      <Modal
        show={showPdf}
        onClose={() => setShowPdf(false)}
        className="h-[90vh] lg:w-[70vw] w-[95vw] p-0 overflow-hidden flex flex-col bg-slate-100 dark:bg-slate-900"
      >
        <div className="flex items-center gap-3 px-5 py-4 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 shrink-0 shadow-sm z-10">
          <div className="p-2 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-xl shrink-0">
            <ReceiptText className="w-5 h-5" />
          </div>
          <div className="flex flex-col">
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
              Dokumen Slip Gaji
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-medium text-slate-500">Milik:</span>
              <Badge
                variant="secondary"
                className="text-[10px] px-2 py-0 uppercase font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                {selectedRow?.hubungan_keluarga?.hubungan_keluarga || "-"}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex-1 w-full relative bg-slate-100 dark:bg-slate-900">
          {selectedRow?.path_slip_gaji ? (
            <PdfViewer url={selectedRow?.path_slip_gaji} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <FileText className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-sm font-medium">Berkas tidak tersedia</p>
            </div>
          )}
        </div>
      </Modal>
    </section>
  );
}
